import { Controller } from '@hotwired/stimulus';

const SCROLL_KEY = 'bulk-select:scrollY';

// "Delete selected" on a carbon footprint consumption table (see
// ConsumptionsBulkSelectHelper). Selection is limited to the rows of the current page.
//
// The tables sit inside the footprint's own <form>, and forms cannot nest, so the DELETE
// form is built and submitted from outside it on demand.
export default class extends Controller {
  static targets = ['all', 'row', 'button', 'count'];
  static values = { url: String, type: String, tab: Number, confirm: String };

  connect() {
    this.refresh();
  }

  toggleAll() {
    this.rowTargets.forEach((checkbox) => (checkbox.checked = this.allTarget.checked));
    this.refresh();
  }

  refresh() {
    const selected = this.selectedIds().length;
    const total = this.rowTargets.length;

    this.allTarget.checked = total > 0 && selected === total;
    this.allTarget.indeterminate = selected > 0 && selected < total;
    this.buttonTarget.hidden = selected === 0;
    this.countTarget.textContent = selected;
  }

  destroy() {
    const ids = this.selectedIds();
    if (ids.length === 0) return;
    if (!window.confirm(this.confirmValue.replace('%{count}', ids.length))) return;

    rememberScroll();
    this.buildForm(ids).requestSubmit();
  }

  selectedIds() {
    return this.rowTargets.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
  }

  buildForm(ids) {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = this.urlValue;
    form.hidden = true;
    // A Turbo submission sends `Accept: text/vnd.turbo-stream.html`, which survives the
    // redirect: #edit would then answer with its turbo_stream branch (form only, no layout)
    // and the flash toast would be consumed without ever being rendered.
    form.dataset.turbo = 'false';

    const fields = [
      ['_method', 'delete'],
      ['authenticity_token', document.querySelector('meta[name="csrf-token"]')?.content],
      ['consumption_type', this.typeValue],
      ['tab', this.tabValue],
      ...ids.map((id) => ['ids[]', id]),
    ];
    fields.forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    return form;
  }
}

// The redirect reloads the whole tab at the top; bring the user back to where the table
// was. Restored on turbo:load, which fires after Turbo's own scroll to the top. The table
// itself may be gone (every row deleted), so this cannot hang off a connecting controller.
function rememberScroll() {
  try {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
  } catch (_e) {
    // Storage blocked: the delete still works, it just doesn't scroll back.
  }
}

document.addEventListener('turbo:load', () => {
  let scrollY;
  try {
    scrollY = sessionStorage.getItem(SCROLL_KEY);
    sessionStorage.removeItem(SCROLL_KEY);
  } catch (_e) {
    return;
  }
  if (scrollY !== null) window.scrollTo(0, Number(scrollY));
});
